import React, { useState, useEffect } from 'react';
import About from './components/About';
import Portfolio from './components/Portfolio';
import Workbench from './components/Workbench';
import Figures from './components/Figures';
import Certifications from './components/Certifications';
import MoreCertificationsPage from './components/MoreCertificationsPage';
import Contact from './components/Contact';
import useFluidCursor from './components/useFluidCursor';
import ScrollProgressBar from './components/ScrollProgressBar';

function App() {
  const [activeSection, setActiveSection] = useState('about');
  
  // Check if we should show the more certifications page
  const showMoreCertifications = window.location.pathname === '/more-certifications';
  
  // Initialize fluid cursor effect
  useFluidCursor();
  
  // Handle scroll to detect which section is in view
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['about', 'portfolio', 'workbench', 'figures', 'certifications', 'contact'];
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionHeight = section.offsetHeight;
          
          // Check if we're in this section (more accurate detection)
          if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
            setActiveSection(sections[i]);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Simple navigation function for internal links
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      window.scrollTo({
        top: section.offsetTop - 80, // Adjust for fixed navbar
        behavior: 'smooth'
      });
    }
  };

  // If we should show the more certifications page, render it
  if (showMoreCertifications) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white overflow-x-hidden">
        <MoreCertificationsPage />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white overflow-x-hidden">
      
      {/* Scroll Progress Bar */}
      <ScrollProgressBar />
      
      {/* Fixed Navigation for quick access */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-black/80 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="text-3xl font-bold gradient-text tracking-wider">
              V1VEK-Y4DAV
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {[
                  { id: 'about', label: 'About' },
                  { id: 'portfolio', label: 'My Portfolio' },
                  { id: 'workbench', label: 'Workbench' },
                  { id: 'figures', label: 'Figures' },
                  { id: 'certifications', label: 'Certifications' },
                  { id: 'contact', label: 'Contact' }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`px-3 py-2 rounded-md text-base font-medium transition-all duration-300 ${activeSection === item.id ? 'bg-gray-900 text-white shadow-lg' : 'text-gray-300 hover:bg-gray-700 hover:text-white'}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content - All sections in one scrollable page */}
      <main className="pt-16">
        <section id="about" className="min-h-screen flex items-center py-8">
          <div className="w-full">
            <About />
          </div>
        </section>
        
        <section id="portfolio" className="min-h-screen flex items-center py-8">
          <div className="w-full">
            <Portfolio />
          </div>
        </section>
        
        <section id="workbench" className="min-h-screen flex items-center py-8">
          <div className="w-full">
            <Workbench />
          </div>
        </section>
        
        <section id="figures" className="min-h-screen flex items-center py-8">
          <div className="w-full">
            <Figures />
          </div>
        </section>
        
        <section id="certifications" className="min-h-screen flex items-center py-8">
          <div className="w-full">
            <Certifications />
          </div>
        </section>
        
        <section id="contact" className="min-h-screen flex items-center py-8">
          <div className="w-full">
            <Contact />
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;